import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import { api } from '../api';

const CartContext = createContext();
const CART_STORAGE_KEY = 'stbarb_cart';
const CART_SESSION_KEY = 'stbarb_cart_session';
const CART_EXPIRY_MS = 2 * 60 * 60 * 1000;

function getSessionId() {
  let id = sessionStorage.getItem(CART_SESSION_KEY);
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(CART_SESSION_KEY, id);
  }
  return id;
}

function cartKey(menuItemId, options, variableAmount) {
  const base = (!options || Object.keys(options).length === 0)
    ? String(menuItemId)
    : `${menuItemId}::${JSON.stringify(options, Object.keys(options).sort())}`;
  if (variableAmount != null) return `${base}::amt:${Number(variableAmount).toFixed(2)}`;
  return base;
}

function loadCart() {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return { items: [] };
    const { items, savedAt } = JSON.parse(raw);
    if (Date.now() - savedAt > CART_EXPIRY_MS) {
      localStorage.removeItem(CART_STORAGE_KEY);
      return { items: [] };
    }
    return { items: items || [] };
  } catch {
    return { items: [] };
  }
}

function saveCart(items) {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({ items, savedAt: Date.now() }));
  } catch { /* storage full or unavailable */ }
}

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD_ITEM': {
      const key = cartKey(action.payload.id, action.payload.options, action.payload.variableAmount);
      const existing = state.items.find((i) => i._key === key);
      if (existing) {
        return {
          ...state,
          items: state.items.map((i) =>
            i._key === key ? { ...i, quantity: i.quantity + 1 } : i
          ),
        };
      }
      return {
        ...state,
        items: [
          ...state.items,
          {
            _key: key,
            menu_item_id: action.payload.id,
            name: action.payload.name,
            price: action.payload.price,
            kitchen: action.payload.kitchen || null,
            venue: action.payload.venue || null,
            options: action.payload.options || null,
            optionsLabel: action.payload.optionsLabel || '',
            variableAmount: action.payload.variableAmount ?? null,
            quantity: 1,
          },
        ],
      };
    }
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter((i) => i._key !== action.payload),
      };
    case 'UPDATE_QUANTITY': {
      if (action.payload.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter((i) => i._key !== action.payload._key),
        };
      }
      return {
        ...state,
        items: state.items.map((i) =>
          i._key === action.payload._key
            ? { ...i, quantity: action.payload.quantity }
            : i
        ),
      };
    }
    case 'CLEAR':
      return { ...state, items: [] };
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, null, loadCart);
  const prevItemsRef = useRef(state.items);

  useEffect(() => {
    saveCart(state.items);

    const sessionId = getSessionId();
    const prev = prevItemsRef.current;
    const curr = state.items;
    prevItemsRef.current = curr;

    const toReserve = [];
    const toRelease = [];

    for (const item of curr) {
      const old = prev.find((p) => p._key === item._key);
      const diff = item.quantity - (old ? old.quantity : 0);
      if (diff > 0) toReserve.push({ menu_item_id: item.menu_item_id, quantity: diff });
    }
    for (const old of prev) {
      const item = curr.find((c) => c._key === old._key);
      if (!item) {
        toRelease.push({ menu_item_id: old.menu_item_id, quantity: old.quantity });
      } else if (old.quantity > item.quantity) {
        toRelease.push({ menu_item_id: old.menu_item_id, quantity: old.quantity - item.quantity });
      }
    }

    if (toReserve.length > 0) {
      api.reserveStock(sessionId, toReserve).catch(() => {});
    }
    if (toRelease.length > 0) {
      api.releaseStock(sessionId, toRelease).catch(() => {});
    }
  }, [state.items]);

  const addItem = useCallback(
    (item) => dispatch({ type: 'ADD_ITEM', payload: item }),
    []
  );
  const removeItem = useCallback(
    (key) => dispatch({ type: 'REMOVE_ITEM', payload: key }),
    []
  );
  const updateQuantity = useCallback(
    (_key, quantity) =>
      dispatch({ type: 'UPDATE_QUANTITY', payload: { _key, quantity } }),
    []
  );
  const clearCart = useCallback(() => dispatch({ type: 'CLEAR' }), []);

  const releaseAll = useCallback(() => {
    const sessionId = getSessionId();
    api.releaseStock(sessionId).catch(() => {});
  }, []);

  const itemCount = state.items.reduce((sum, i) => sum + i.quantity, 0);
  const total = state.items.reduce(
    (sum, i) => sum + i.price * i.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{
        items: state.items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        releaseAll,
        itemCount,
        total,
        cartKey,
        sessionId: getSessionId(),
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
}
